import { GraphDetailSelection } from "./graphDetialsSelectionHook";
import {
  GraphDataEdge,
  GraphDataNode,
  NodeType,
  QualifiedFeatureFragment,
  QualifiedFeatureValue,
  RuleType,
} from "fe-common/graphql";
import { isPopulationFeature, Relations } from "shared";
import { RuleStatFragment } from "fe-common/graphql/types";

function edgePercentRange(e: GraphDataEdge) {
  let val = (rv: RuleStatFragment) =>
    e.type === RuleType.Lift ? rv.lift : rv.prob;
  let min = e.ruleValues ? Math.min(...e.ruleValues.map(val)) : 1;
  let max = e.ruleValues ? Math.max(...e.ruleValues.map(val)) : 1;
  return { min, max };
}

function isExactEdge(e: GraphDataEdge) {
  return (
    e.ruleValues &&
    e.ruleValues.find((r) => e.ruleValueId === r.ruleValue.ruleValueId)
      ?.ruleValue.exactCitation
  );
}

export function computeSummaryInfo({
  selection,
  directEdge,
  disease,
  findingNode,
}: GraphDetailSelection): {
  source: GraphDataNode;
  targets: {
    target: GraphDataNode;
    ruletype: RuleType;
    metric: number;
    min: number;
    max: number;
    exact: boolean;
  }[];
} {
  let edge: {
    path: GraphDataEdge;
    source: GraphDataNode;
    target: GraphDataNode;
  } = selection?.edge?.[0];
  let node = selection?.node;

  let targets: any[] = [];
  let source: GraphDataNode = null;
  // compute selection parameters
  if (edge) {
    source = edge.source;

    // if this is etiology rule, we have previously flipped source and target to display correctly
    // on graph. So need to unflip it now so texts come out right...
    if (edge.path.type === RuleType.TargetCauseSource) {
      let b = source;
      source = edge.target;
      edge.target = b;
    }

    let { min, max } = edgePercentRange(edge.path);
    targets.push({
      target: edge.target,
      ruletype: edge.path.type,
      metric:
        edge.path.type === RuleType.Lift ? edge.path.lift : edge.path.prob,
      min,
      max,
      exact: isExactEdge(edge.path),
    });
  } else if (node) {
    source = disease;
    if (node.type === NodeType.Disease) {
      // no targets
    } else {
      let direct = directEdge(node);
      targets.push({
        target: node,
        ruletype: direct?.type,
        metric: node.probability,
        min: node.percentRange?.min,
        max: node.percentRange?.max,
        exact: direct != null && isExactEdge(direct),
      });
    }
  } else if (findingNode) {
    source = disease;
    targets = findingNode.map((n) => ({
      target: n,
      ruletype: directEdge(n)?.type,
      metric: n.probability,
      min: n.percentRange?.min,
      max: n.percentRange?.max,
      exact: directEdge(n) != null && isExactEdge(directEdge(n)),
    }));
  }
  return { source, targets };
}

function populationRelation({ id }: { id: string }) {
  switch (id) {
    case Relations.AGE_OF_ONSET:
      return { title: "Age (onset)", order: 1 };
    case Relations.PERIODS_OF_LIFE:
      return { title: "Age", order: 1 };
    case Relations.CARE_SETTING:
      return { title: "Care Setting", order: 2 };
    case Relations.GEOGRAPHIC:
      return { title: "Region", order: 3 };
    default:
      return null;
  }
}

export function cutPopulationFromFeatures(
  qffs: QualifiedFeatureFragment[],
): {
  features: QualifiedFeatureFragment[];
  populationQualifiers: { title: string; value: QualifiedFeatureValue }[];
} {
  let populationQualifiers = [];
  let features = [];
  for (let qf of qffs) {
    let qualifiers = [];
    if (qf.qualifiers) {
      // find all populationRelations that we have special handling for
      for (let q of qf.qualifiers) {
        let rel = populationRelation(q.relation);
        if (rel) populationQualifiers.push({ ...rel, value: q.value });
        else qualifiers.push(q);
      }
    }
    if (!isPopulationFeature(qf.feature)) {
      features.push({ ...qf, qualifiers });
    }
  }
  populationQualifiers.sort((a, b) => a.order - b.order);
  return {
    populationQualifiers,
    features,
  };
}

export function inexactProbShort(p: number) {
  if (p > 0.7) return "Very Common";
  else if (p > 0.6) return "Common";
  else if (p > 0.05) return "Often";
  else if (p > 0.01) return "Uncommon";
  else return "Rare";
}

export function inexactProb(p: number) {
  if (p >= 0.75) return "Very Common";
  else if (p >= 0.5) return "Common";
  else if (p >= 0.25) return "Probable";
  else if (p >= 0.1) return "Infrequent";
  else return "Rare";
}

export function inexactLift(l: number) {
  if (l > 10) return "with strong lift";
  else if (l > 1.5) return "with positive lift";
  else if (l > 1) return "with observed lift";
  else if (l < 1) return "with negative lift";
  else return "no lift";
}

export const rule = (type: RuleType) => {
  switch (type) {
    case RuleType.Isa:
      return "is a type of ";
    case RuleType.Association:
      return "is associated with";
    case RuleType.SourceCauseTarget:
      return "causes";
    case RuleType.TargetCauseSource:
      return "is caused by";
    case RuleType.Lift:
      return "is a factor for";
    case RuleType.Subtype:
      return "includes";
    case RuleType.Companion:
      return "is similar to";
  }
};
